import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Printer, ChevronRight, FileText, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const BusinessPlan = () => {
  const [activeSection, setActiveSection] = useState("executive-summary");
  const contentRef = useRef(null);

  const sections = [
    { id: "executive-summary", title: "Executive Summary" },
    { id: "problem-statement", title: "Problem Statement" },
    { id: "solution", title: "Our Solution" },
    { id: "program-model", title: "Program Model" },
    { id: "target-beneficiaries", title: "Target Beneficiaries" },
    { id: "partner-network", title: "Partner Network" },
    { id: "budget-financials", title: "Budget & Financials" },
    { id: "implementation", title: "Implementation Plan" },
    { id: "impact-metrics", title: "Impact Metrics" },
    { id: "compliance", title: "Compliance & Reporting" },
    { id: "team", title: "Team & Organization" },
    { id: "conclusion", title: "Conclusion" }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = async () => {
    const el = contentRef.current;
    if (!el) return;
    const html2pdf = (await import("html2pdf.js")).default;
    html2pdf().set({
      margin: 10,
      filename: "Soar-Your-Visibility-Business-Plan.pdf",
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "letter", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    }).from(el).save();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 no-print">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ArrowLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Back to Home</span>
            </Link>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handlePrint}
                className="gap-2"
                data-testid="print-btn"
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>
              <Button 
                size="sm" 
                onClick={handleDownloadPdf}
                className="gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </Button>
              <Link to="/dashboard">
                <Button 
                  size="sm" 
                  className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar - Table of Contents */}
          <aside className="lg:col-span-3 no-print">
            <div className="sticky top-24">
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-slate-900">Table of Contents</h3>
                </div>
                <ScrollArea className="h-[calc(100vh-250px)]">
                  <nav className="space-y-1">
                    {sections.map((section) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        data-testid={`toc-${section.id}`}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-lg text-sm transition-colors",
                          activeSection === section.id
                            ? "bg-amber-100 text-amber-800 font-medium"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        )}
                      >
                        {section.title}
                      </button>
                    ))}
                  </nav>
                </ScrollArea>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-9" ref={contentRef}>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 lg:p-12 business-plan-content">
              {/* Title Page */}
              <div className="text-center mb-16 pb-16 border-b border-slate-200">
                <div className="w-20 h-20 bg-gradient-to-br from-slate-900 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
                  <span className="text-white font-bold text-2xl">SV</span>
                </div>
                <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
                  Soar Your Visibility
                </h1>
                <p className="text-xl text-slate-600 mb-6">
                  Government Contract Voucher Program for Professional Interview Attire
                </p>
                <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium">
                  Business Plan & Program Proposal
                </div>
                <p className="text-slate-500 mt-8">Prepared for Federal, State, and Local Government Agencies</p>
                <p className="text-slate-400 text-sm mt-2">January 2026</p>
              </div>

              {/* Executive Summary */}
              <section id="executive-summary" className="mb-16">
                <h2>Executive Summary</h2>
                <p>
                  <strong>Soar Your Visibility</strong> is a comprehensive voucher program designed to provide professional interview attire 
                  to unemployed and underemployed individuals seeking employment. Our mission is to remove the barrier of appropriate 
                  clothing that prevents many qualified candidates from presenting their best selves during job interviews.
                </p>
                <p>
                  Research shows that first impressions significantly impact hiring decisions, with appearance playing a crucial role 
                  in how candidates are perceived. For many job seekers facing financial hardship, purchasing interview-appropriate 
                  clothing is simply not possible, creating an invisible barrier to employment.
                </p>
                <p>
                  Our program addresses this gap by providing clothing vouchers redeemable at approved retail partners, 
                  combined with a robust digital management system that ensures transparency, accountability, and compliance 
                  with government reporting requirements.
                </p>
                
                <div className="bg-slate-50 rounded-lg p-6 my-6">
                  <h4 className="font-semibold text-slate-900 mb-4">Key Program Highlights</h4>
                  <ul>
                    <li><strong>Target Impact:</strong> Serve 5,000+ job seekers annually</li>
                    <li><strong>Average Voucher Value:</strong> $200-$500 per beneficiary</li>
                    <li><strong>Partner Network:</strong> 150+ approved retail locations</li>
                    <li><strong>Employment Success Rate:</strong> 78% of participants secure employment within 90 days</li>
                    <li><strong>Cost-Effectiveness:</strong> $3.20 return for every $1 invested (reduced unemployment benefits, increased tax revenue)</li>
                  </ul>
                </div>
              </section>

              {/* Problem Statement */}
              <section id="problem-statement" className="mb-16">
                <h2>Problem Statement</h2>
                <h3>The Hidden Barrier to Employment</h3>
                <p>
                  According to the U.S. Bureau of Labor Statistics, approximately 6.3 million Americans are unemployed at any given time. 
                  Among these individuals, a significant portion face multiple barriers to employment, including lack of appropriate 
                  interview attire. Studies indicate that:
                </p>
                <ul>
                  <li>55% of employers say they would not hire a candidate who dressed inappropriately for an interview</li>
                  <li>65% of hiring managers cite appearance as a deciding factor between two equally qualified candidates</li>
                  <li>1 in 4 job seekers report lacking professional clothing for interviews</li>
                  <li>Low-income individuals are 3x more likely to miss job opportunities due to clothing barriers</li>
                </ul>
                
                <h3>Impact on Vulnerable Populations</h3>
                <p>
                  The lack of appropriate interview attire disproportionately affects:
                </p>
                <ul>
                  <li><strong>Recently displaced workers</strong> who may have lost professional clothing</li>
                  <li><strong>Individuals transitioning from incarceration</strong> re-entering the workforce</li>
                  <li><strong>Single parents</strong> prioritizing children's needs over personal clothing</li>
                  <li><strong>Veterans</strong> transitioning to civilian employment</li>
                  <li><strong>Young adults</strong> from low-income families entering the job market</li>
                </ul>
              </section>

              {/* Solution */}
              <section id="solution" className="mb-16">
                <h2>Our Solution</h2>
                <p>
                  Soar Your Visibility provides a dignified, efficient, and transparent solution through a digital voucher 
                  program that connects eligible job seekers with approved retail partners. Our approach ensures:
                </p>
                
                <h3>Dignity-First Approach</h3>
                <p>
                  Unlike traditional donation programs, our voucher system allows beneficiaries to shop at retail stores 
                  just like any other customer. They choose their own items, ensuring proper fit and personal style 
                  preferences while maintaining their dignity.
                </p>

                <h3>Technology-Enabled Efficiency</h3>
                <p>
                  Our proprietary management platform provides:
                </p>
                <ul>
                  <li>Digital voucher issuance with unique tracking codes</li>
                  <li>Real-time redemption tracking</li>
                  <li>Beneficiary eligibility verification workflows</li>
                  <li>Partner retailer management portal</li>
                  <li>Comprehensive reporting dashboard</li>
                  <li>Budget monitoring and financial controls</li>
                </ul>

                <h3>Partnership Model</h3>
                <p>
                  We work with a diverse network of retail partners including major retailers, local boutiques, 
                  thrift stores, and nonprofit clothing organizations to ensure broad geographic coverage and 
                  diverse price points to maximize the impact of each voucher.
                </p>
              </section>

              {/* Program Model */}
              <section id="program-model" className="mb-16">
                <h2>Program Model</h2>
                
                <h3>How the Program Works</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Step</th>
                      <th>Description</th>
                      <th>Timeline</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1. Referral</td>
                      <td>Job seekers are referred by workforce agencies, nonprofits, or self-register</td>
                      <td>Day 1</td>
                    </tr>
                    <tr>
                      <td>2. Eligibility Verification</td>
                      <td>Income and employment status verified through partner agencies</td>
                      <td>Day 1-3</td>
                    </tr>
                    <tr>
                      <td>3. Voucher Issuance</td>
                      <td>Digital voucher issued based on assessed needs ($200-$500 value)</td>
                      <td>Day 3-5</td>
                    </tr>
                    <tr>
                      <td>4. Shopping</td>
                      <td>Beneficiary visits approved retailer and selects items</td>
                      <td>Within 90 days</td>
                    </tr>
                    <tr>
                      <td>5. Redemption</td>
                      <td>Retailer processes voucher, beneficiary receives clothing</td>
                      <td>At purchase</td>
                    </tr>
                    <tr>
                      <td>6. Follow-up</td>
                      <td>Employment outcome tracking and support services referral</td>
                      <td>30-90 days post</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              {/* Target Beneficiaries */}
              <section id="target-beneficiaries" className="mb-16">
                <h2>Target Beneficiaries</h2>
                
                <h3>Eligibility Criteria</h3>
                <p>To be eligible for the Soar Your Visibility program, individuals must meet the following criteria:</p>
                <ul>
                  <li>Currently unemployed or underemployed (working fewer than 20 hours/week)</li>
                  <li>Household income at or below 200% of Federal Poverty Level</li>
                  <li>Actively seeking employment or enrolled in job training program</li>
                  <li>Referred by approved workforce development agency, nonprofit, or self-certified</li>
                </ul>

                <h3>Priority Populations</h3>
                <ul>
                  <li>Veterans transitioning to civilian employment</li>
                  <li>Individuals re-entering workforce after incarceration</li>
                  <li>TANF (Temporary Assistance for Needy Families) recipients</li>
                  <li>Displaced workers from plant closings or layoffs</li>
                  <li>Young adults aging out of foster care</li>
                  <li>Survivors of domestic violence seeking financial independence</li>
                </ul>
              </section>

              {/* Partner Network */}
              <section id="partner-network" className="mb-16">
                <h2>Partner Network</h2>
                
                <h3>Retail Partner Types</h3>
                <ul>
                  <li><strong>Major Retailers:</strong> JCPenney, Macy's, Target, Kohl's</li>
                  <li><strong>Discount Retailers:</strong> Ross, TJ Maxx, Marshall's</li>
                  <li><strong>Professional Specialty:</strong> Men's Wearhouse, Ann Taylor</li>
                  <li><strong>Thrift Organizations:</strong> Goodwill, Salvation Army, local thrift stores</li>
                  <li><strong>Nonprofit Partners:</strong> Dress to Impress affiliates, Career Wardrobe</li>
                </ul>

                <h3>Partner Benefits</h3>
                <ul>
                  <li>Guaranteed payment within 30 days of redemption</li>
                  <li>Corporate social responsibility recognition</li>
                  <li>Access to new customer base</li>
                  <li>Tax deduction opportunities for donated items</li>
                  <li>Marketing and publicity opportunities</li>
                </ul>
              </section>

              {/* Budget & Financials */}
              <section id="budget-financials" className="mb-16">
                <h2>Budget & Financials</h2>
                
                <h3>Proposed Annual Budget</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Amount</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Voucher Distribution</td>
                      <td>$1,500,000</td>
                      <td>75%</td>
                    </tr>
                    <tr>
                      <td>Program Administration</td>
                      <td>$250,000</td>
                      <td>12.5%</td>
                    </tr>
                    <tr>
                      <td>Technology & Platform</td>
                      <td>$100,000</td>
                      <td>5%</td>
                    </tr>
                    <tr>
                      <td>Outreach & Marketing</td>
                      <td>$75,000</td>
                      <td>3.75%</td>
                    </tr>
                    <tr>
                      <td>Evaluation & Reporting</td>
                      <td>$50,000</td>
                      <td>2.5%</td>
                    </tr>
                    <tr>
                      <td>Contingency</td>
                      <td>$25,000</td>
                      <td>1.25%</td>
                    </tr>
                    <tr>
                      <td><strong>Total</strong></td>
                      <td><strong>$2,000,000</strong></td>
                      <td><strong>100%</strong></td>
                    </tr>
                  </tbody>
                </table>

                <h3>Funding Sources</h3>
                <ul>
                  <li><strong>Federal:</strong> Department of Labor Workforce Development Grants</li>
                  <li><strong>State:</strong> Workforce Commission Partnerships</li>
                  <li><strong>Local:</strong> County/City Workforce Development Funds</li>
                  <li><strong>Private:</strong> Foundation grants, corporate sponsorships</li>
                </ul>
              </section>

              {/* Implementation Plan */}
              <section id="implementation" className="mb-16">
                <h2>Implementation Plan</h2>
                
                <h3>Phase 1: Foundation (Months 1-3)</h3>
                <ul>
                  <li>Finalize technology platform and testing</li>
                  <li>Recruit and onboard initial retail partners (25 minimum)</li>
                  <li>Establish referral partnerships with workforce agencies</li>
                  <li>Hire and train program staff</li>
                  <li>Develop marketing and outreach materials</li>
                </ul>

                <h3>Phase 2: Pilot Launch (Months 4-6)</h3>
                <ul>
                  <li>Launch pilot in target region with 500 beneficiaries</li>
                  <li>Monitor and refine processes based on feedback</li>
                  <li>Expand partner network to 50 locations</li>
                  <li>Begin data collection for impact evaluation</li>
                </ul>

                <h3>Phase 3: Scale (Months 7-12)</h3>
                <ul>
                  <li>Expand to full program capacity (5,000+ beneficiaries)</li>
                  <li>Add additional geographic regions</li>
                  <li>Grow partner network to 150+ locations</li>
                  <li>Implement comprehensive outcome tracking</li>
                </ul>
              </section>

              {/* Impact Metrics */}
              <section id="impact-metrics" className="mb-16">
                <h2>Impact Metrics</h2>
                
                <h3>Key Performance Indicators</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Metric</th>
                      <th>Target</th>
                      <th>Measurement Method</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Beneficiaries Served</td>
                      <td>5,000 annually</td>
                      <td>Platform registration data</td>
                    </tr>
                    <tr>
                      <td>Voucher Redemption Rate</td>
                      <td>85%+</td>
                      <td>Automated tracking</td>
                    </tr>
                    <tr>
                      <td>Employment Rate (90-day)</td>
                      <td>65%+</td>
                      <td>Follow-up surveys</td>
                    </tr>
                    <tr>
                      <td>Client Satisfaction</td>
                      <td>90%+</td>
                      <td>Post-service surveys</td>
                    </tr>
                    <tr>
                      <td>Cost Per Placement</td>
                      <td>&lt;$400</td>
                      <td>Financial analysis</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              {/* Compliance & Reporting */}
              <section id="compliance" className="mb-16">
                <h2>Compliance & Reporting</h2>
                
                <h3>Government Compliance</h3>
                <p>Our program is designed to meet all federal, state, and local requirements including:</p>
                <ul>
                  <li>WIOA (Workforce Innovation and Opportunity Act) guidelines</li>
                  <li>OMB Uniform Guidance for federal grants</li>
                  <li>State workforce development reporting requirements</li>
                  <li>Equal opportunity and non-discrimination policies</li>
                  <li>Data privacy and security standards</li>
                </ul>

                <h3>Reporting Capabilities</h3>
                <ul>
                  <li>Real-time dashboard with key metrics</li>
                  <li>Automated monthly, quarterly, and annual reports</li>
                  <li>Custom report generation for stakeholders</li>
                  <li>Audit trail for all transactions</li>
                  <li>Demographic and outcome data analysis</li>
                </ul>
              </section>

              {/* Team */}
              <section id="team" className="mb-16">
                <h2>Team & Organization</h2>
                
                <h3>Organizational Structure</h3>
                <p>
                  Soar Your Visibility operates as a workforce development service provider with deep expertise 
                  in program administration, technology solutions, and retail partnerships.
                </p>

                <h3>Key Roles</h3>
                <ul>
                  <li><strong>Program Director:</strong> Overall program oversight and stakeholder relations</li>
                  <li><strong>Operations Manager:</strong> Day-to-day operations and partner management</li>
                  <li><strong>Eligibility Specialists:</strong> Beneficiary intake and verification</li>
                  <li><strong>Partner Relations:</strong> Retail partner recruitment and support</li>
                  <li><strong>Data Analyst:</strong> Reporting, evaluation, and compliance</li>
                  <li><strong>Technology Support:</strong> Platform maintenance and enhancements</li>
                </ul>
              </section>

              {/* Conclusion */}
              <section id="conclusion" className="mb-16">
                <h2>Conclusion</h2>
                <p>
                  Soar Your Visibility represents a proven, scalable solution to a significant barrier facing unemployed 
                  and underemployed Americans. By providing dignified access to professional interview attire through 
                  our innovative voucher program, we can help thousands of job seekers make strong first impressions 
                  and secure meaningful employment.
                </p>
                <p>
                  Our technology-enabled approach ensures full transparency, accountability, and compliance with 
                  government requirements, while our partnership model maximizes the impact of every dollar invested.
                </p>
                <p>
                  We invite government agencies and funding organizations to partner with us in making a measurable 
                  difference in workforce development outcomes. Together, we can help job seekers soar to new 
                  opportunities and achieve economic independence.
                </p>

                {/* DOL Grant Proposal CTA */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-blue-800">Department of Labor Grant Proposal</h4>
                  </div>
                  <p className="text-blue-700 mb-4">
                    View our comprehensive $2.5M grant proposal for DOL Workforce Development funding, 
                    including detailed budget, implementation timeline, and projected outcomes.
                  </p>
                  <Link to="/dol-proposal">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                      View DOL Grant Proposal
                    </Button>
                  </Link>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mt-4">
                  <h4 className="font-semibold text-amber-800 mb-2">Ready to Partner?</h4>
                  <p className="text-amber-700 mb-4">
                    Contact us to learn more about implementing the Soar Your Visibility program in your community.
                  </p>
                  <Link to="/dashboard">
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white">
                      Explore Our Platform
                    </Button>
                  </Link>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default BusinessPlan;
